import React, { useEffect, useMemo, useState } from 'react';
import { Button, Field, Header, Image, Input, Message, Placeholder } from 'react-frontier';
import { PayfrontProvider, useCardData, usePayfront } from 'react-payfront';
import { useParams, useSearchParams } from 'react-router-dom';
import { CDN_URL } from '../PublicAPI';
import { useTranslation } from 'react-i18next';
import { addCommas, bindFormChange, formatCreditCard, formatExpiration, isJWT } from '../PublicUtil';
import API from '../PublicAPI';
import classNames from 'classnames';
import '../style/request.scss';

interface PaymentRequest{
	request_id: number,
	client_id: number,
	client_name: string,
	public_key: string,
	amount: number,
	description: string,
	payments: number,
	max_payments: number,
	expiration_date: number,
}

var PayRequest = ()=>{
	var { t } = useTranslation();
	var { hash } = useParams<{ hash: string }>();
	var { createToken } = usePayfront();
	var [searchParams] = useSearchParams();
	var [request, setRequest] = useState<PaymentRequest>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	var [card, setCard] = useCardData();

	var hash_valid = useMemo(()=>{
		return isJWT(hash);
	}, [hash]);

	useEffect(()=>{
		if(!hash_valid) return;
		API.getPaymentRequest(hash).then(res=>{
			if(res.error) return setLoadError(res.message);
			setRequest(res.data);
		}).catch(err=>{
			setLoadError(t('common.unexpected_error', { code: 'PYRQ-1' }));
		});
	}, []);

	var submit = async (setLoading: (l: boolean)=>void)=>{
		if(!request) return;
		setLoading(true);
		try{
			var { error, prompts, message, data } = await createToken({
				...card,
				amount: request.amount,
				api_key: request.public_key,
			});
			if(prompts && prompts.length>0){
				setLoading(false);
				return setErrorPrompts(prompts);
			}else if(error && message){
				setLoading(false);
				return setErrorPrompts([message]);
			}
			setErrorPrompts([]);
			var card_token = data.token;
			if(!card_token) return setErrorPrompts([t('common.unexpected_error', { code: 'PYRQ-2' })]);
		}catch(e){
			setLoading(false);
			return setErrorPrompts([t('common.unexpected_error', { code: 'PYRQ-3' })]);
		}

		try{
			var res = await API.createRequestPayment(hash, card_token);
			setLoading(false);
			if(res.error) return setErrorPrompts([res.message]);
			if(!res.data?.redirect){
				return setErrorPrompts([t('common.unexpected_error', { code: 'PYRQ-4' })]);
			}
			window.location.href = res.data.redirect;
		}catch(e){
			return setErrorPrompts([t('common.unexpected_error', { code: 'PYRQ-5' })]);
		}finally{
			setLoading(false);
		}
	}

	var client_id = useMemo(()=>{
		if(!request){
			var clpr = searchParams.get('c');
			if(!clpr) return null;
			var numclpr = parseInt(clpr);
			if(Number.isNaN(numclpr)) return null;
			return numclpr;
		}else{
			return request.client_id;
		}
	}, [request]);

	var onCardChange = bindFormChange(card, setCard);
	
	return <div className="pf pay-request-screen">
		<div className="client">
			{client_id ? (
				<Image src={`${CDN_URL}/clients/${client_id}.webp`} fallbackComponent={(
					<div className='placeholder' />
				)} />
			) : (
				<div className='placeholder' />
			)}
		</div>
		<div className="contents">
			<div className="amount">
				<div className="label">{t('request.to_pay')}</div>
				<div className="amount">
					{request ? `$${addCommas(request.amount)}` : (
						<Placeholder height={40} width={120} />
					)}
				</div>
				<div className="currency">MXN</div>
			</div>
			<div className={classNames("form", { loading: !request })}>
				{request ? <>
					<Input label={t('card.card_name')} value={card.card_name} onChange={onCardChange('card_name')} />
					<Input label={t('card.card_number')} value={card.card_number} onChange={onCardChange('card_number')} maxLength={16+3} valueFormat={formatCreditCard} />
					<Field amount={2}>
						<Input label={t('card.expiration')} placeholder='MM/YY' value={card.expiration} onChange={onCardChange('expiration')} valueFormat={formatExpiration} maxLength={5} />
						<Input label={t('card.security_code')} placeholder='CVV' value={card.card_cvv} onChange={onCardChange('card_cvv')} />
					</Field>
					<Input label={t('card.postal_code')} onChange={onCardChange('postal_code')} value={card.postal_code} />
					<Message list={errorPrompts} type='error' />
					<Button color='black' text={t('request.pay')} onClick={submit} />
				</> : (
					!hash_valid ? (
						<Header text={'Error'} subtext={t('request.invalid_link')} />
					) : loadError ? (
						<Header text={'Error'} subtext={loadError} />
					) : <>
						<div className="fr loading big inline"></div>
						<Header text={'Cargando'} className='loading' />
					</>
				)}
			</div>
		</div>
	</div>
}

var PayRequestContainer = ()=>{
	return <PayfrontProvider apiKey={null} baseUrl={process.env.REACT_APP_API_URL}>
		<PayRequest />
	</PayfrontProvider>
}

export default PayRequestContainer;